<template>
  <div class="operation-log-list">
    <a-spin :spinning="loading" tip="加载中...">
      <div class="log-container">
        <div v-if="logList.length === 0" class="empty-state">
          <a-empty description="暂无操作日志" />
        </div>
        <div v-else class="log-list">
          <div
            v-for="(item, index) in logList"
            :key="item.id || index"
            class="log-item"
          >
            <div class="log-header">
              <div class="teacher-info">
                <img
                  v-if="item.teacher && item.teacher.head_img"
                  :src="item.teacher.head_img"
                  class="teacher-avatar"
                  alt="头像"
                />
                <div v-else class="teacher-avatar default-avatar">
                  {{ (item.teacher && item.teacher.teacher_name ? item.teacher.teacher_name : '?').charAt(0) }}
                </div>
                <span class="teacher-name">
                  {{ item.teacher ? item.teacher.teacher_name : '未知老师' }}
                </span>
              </div>
              <div class="log-time" v-if="item.create_time">
                <a-icon type="clock-circle" />
                {{ formatTime(item.create_time) }}
              </div>
            </div>

            <div class="log-content">
              <div class="log-main-text">
                <span class="text-normal">对</span>
                <span class="element-type">{{ item.element_type_labels || '未知组件' }}</span>
                <span class="text-normal">进行了</span>
                <span class="action-type">{{ item.actions || '未知操作' }}</span>
                <span class="text-normal">操作</span>
              </div>

              <div v-if="item.change_diff" class="log-detail">
                <span class="detail-label">文本内容：</span>
                <a-tooltip :title="item.change_diff.textarea.to" placement="top">
                  <span class="detail-value">从 <span class="from-text">{{ item.change_diff.textarea.from || '空' }}</span>  变更为 <span class="to-text">{{ item.change_diff.textarea.to || '无' }}</span></span>
                </a-tooltip>
              </div>

              <div v-if="item.target_ref" class="log-detail">
                <span class="detail-label">操作对象：</span>
                <a-tooltip :title="item.target_ref" placement="top">
                  <span class="detail-value">{{ truncateTargetRef(item.target_ref) }}</span>
                </a-tooltip>
              </div>

              <div v-if="item.student_names" class="log-detail">
                <span class="detail-label">操作学生：</span>
                <a-tooltip v-if="getStudentCount(item.student_names) > 3" placement="topLeft">
                  <template slot="title">
                    <div class="tooltip-students">{{ item.student_names }}</div>
                  </template>
                  <span class="student-names">{{ formatStudentNames(item.student_names) }}</span>
                </a-tooltip>
                <span v-else class="student-names">{{ formatStudentNames(item.student_names) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-spin>

    <div v-if="pagination && pagination.total > 0" class="pagination-wrapper">
      <a-pagination
        show-size-picker
        :current="pagination.current"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :page-size-options="pageSizeOptions"
        @change="handlePageChange"
        @show-size-change="handlePageSizeChange"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'OperationLogList',
  props: {
    logList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: [Object, Boolean],
      default: () => ({
        total: 0,
        current: 1,
        pageSize: 10
      })
    }
  },
  data() {
    return {
      pageSizeOptions: ['10', '20', '50', '100']
    }
  },
  methods: {
    handlePageChange(current, pageSize) {
      this.$emit('pagination-change', {
        ...this.pagination,
        current,
        pageSize
      });
    },
    handlePageSizeChange(current, pageSize) {
      this.$emit('pagination-change', {
        ...this.pagination,
        current: 1,
        pageSize
      });
    },
    // 格式化时间显示
    formatTime(timeStr) {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      
      if (minutes < 1) return '刚刚';
      if (minutes < 60) return `${minutes}分钟前`;
      if (hours < 24) return `${hours}小时前`;
      if (days < 7) return `${days}天前`;
      return timeStr.split(' ')[0];
    },
    // 提取操作详情
    extractActionDetail(actions) {
      if (!actions) return '';
      // 尝试提取括号内的内容，如 "编辑(文本框位置,尺寸)" -> "文本框位置,尺寸"
      const match = actions.match(/[（(](.*?)[)）]/);
      return match ? match[1] : '';
    },
    // 截断target_ref显示
    truncateTargetRef(ref) {
      if (!ref) return '';
      if (ref.length <= 30) return ref;
      return ref.substring(0, 15) + '...' + ref.substring(ref.length - 10);
    },
    // 获取学生数量
    getStudentCount(text) {
      if (!text) return 0;
      if (Array.isArray(text)) return text.length;
      return text.split(/[、,，]/).filter(n => n.trim()).length;
    },
    // 格式化学生名字显示，超过3个时只显示前3个
    formatStudentNames(text) {
      if (!text) return '-';
      
      let names = [];
      if (Array.isArray(text)) {
        names = text;
      } else if (typeof text === 'string') {
        names = text.split(/[、,，]/).map(n => n.trim()).filter(Boolean);
      }
      
      const totalCount = names.length;
      const maxDisplayCount = 3;
      
      if (totalCount <= maxDisplayCount) {
        return names.join('、');
      } else {
        const displayNames = names.slice(0, maxDisplayCount).join('、');
        return `${displayNames} 等${totalCount}人`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.operation-log-list {
  width: 100%;
  padding: 10px;
  
  ::v-deep .ant-spin-nested-loading {
    overflow: hidden;
  }
  
  ::v-deep .ant-spin-container {
    min-height: 200px;
  }
  
  .log-container {
    min-height: 200px;
  }
  
  .log-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .log-item {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 16px;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: #1890ff;
      box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
    }
  }
  
  .log-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px dashed #f0f0f0;
  }
  
  .teacher-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .teacher-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    
    &.default-avatar {
      background: #1890ff;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 500;
    }
  }
  
  .teacher-name {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
  }
  
  .log-time {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #999;
  }
  
  .log-content {
    font-size: 14px;
    line-height: 1.8;
  }
  
  .log-main-text {
    color: #333;
    margin-bottom: 8px;
  }
  
  .text-normal {
    color: #666;
  }
  
  .element-type {
    color: #1890ff;
    font-weight: 600;
    margin: 0 2px;
  }
  
  .action-type {
    color: #52c41a;
    font-weight: 600;
    margin: 0 2px;
  }
  
  .action-detail {
    color: #fa8c16;
    font-size: 13px;
  }
  
  .log-detail {
    display: flex;
    align-items: flex-start;
    margin-top: 4px;
    font-size: 13px;
  }
  
  .detail-label {
    color: #999;
    flex-shrink: 0;
  }
  
  .detail-value {
    color: #666;
    word-break: break-all;
  }
  
  .student-names {
    color: #722ed1;
    font-weight: 500;
    cursor: pointer;
    
    &:hover {
      color: #9254de;
    }
  }

  .from-text {
    color: #9254de;
  }
  
  .to-text {
    color: #9254de;
  }
  
  .tooltip-students {
    max-width: 300px;
    word-break: break-all;
    line-height: 1.6;
  }
  
  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0;
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
}
</style>
